<script setup lang="ts">
  import { cva } from 'class-variance-authority'
  import { useElementBounding, useElementVisibility, useWindowSize } from '@vueuse/core'
  import type { FeatureBannerStoryblok } from '@/storyblok/component-types-sb'
  import { toAssetImageProps } from '@/utils/storyblok/normalizers'
  import { useParallaxTransform } from '@/composables/useParallaxTransform'

  const props = defineProps<{
    blok: FeatureBannerStoryblok
  }>()

  const mobileImageContainerRef = ref<HTMLElement | null>(null)
  const desktopImageContainerRef = ref<HTMLElement | null>(null)
  const { isDesktop, isTablet } = useBTBreakpoints()
  const { height: viewportHeight } = useWindowSize()
  const { top: mobileImageTop, height: mobileImageHeight } = useElementBounding(mobileImageContainerRef)
  const { top: desktopImageTop, height: desktopImageHeight } = useElementBounding(desktopImageContainerRef)
  const isMobileImageVisible = useElementVisibility(mobileImageContainerRef)
  const isDesktopImageVisible = useElementVisibility(desktopImageContainerRef)

  const ctaButton = computed(() => props.blok.ctaButton?.[0] ?? null)
  const imageProps = computed(() => toAssetImageProps(props.blok.image))
  const hasParallaxEffect = computed(() => Boolean(props.blok.hasParallaxEffect))
  const mobileParallaxMaxOffsetRatio = computed(() => (isTablet.value ? 0.15 : 0.2))

  const mobileImageTransform = useParallaxTransform({
    elementTop: mobileImageTop,
    elementHeight: mobileImageHeight,
    viewportHeight,
    maxOffsetRatio: mobileParallaxMaxOffsetRatio,
    disabled: computed(() => !hasParallaxEffect.value || isDesktop.value || !isMobileImageVisible.value)
  })

  const desktopImageTransform = useParallaxTransform({
    elementTop: desktopImageTop,
    elementHeight: desktopImageHeight,
    viewportHeight,
    maxOffsetRatio: 0.15,
    disabled: computed(() => !hasParallaxEffect.value || !isDesktop.value || !isDesktopImageVisible.value)
  })

  const backgroundClassCva = cva(
    'relative mx-auto flex flex-col overflow-hidden md:block md:w-11/12 md:rounded-md xl:rounded-2md md:p-24 lg:w-full lg:px-24 lg:py-14 xl:py-28',
    {
      variants: {
        backgroundColor: {
          gray: 'md:bg-grey',
          green: 'md:bg-bt-green'
        }
      }
    }
  )

  const mobileImageWrapperClassCva = cva('overflow-hidden relative rounded-sm sm:rounded-md md:hidden h-48 sm:h-62', {
    variants: {
      backgroundColor: {
        gray: 'bg-grey',
        green: 'bg-bt-green'
      }
    }
  })

  const backgroundClass = computed(() =>
    backgroundClassCva({
      backgroundColor: props.blok.backgroundColor
    })
  )

  const mobileImageWrapperClass = computed(() =>
    mobileImageWrapperClassCva({
      backgroundColor: props.blok.backgroundColor
    })
  )
</script>

<template>
  <div :class="backgroundClass">
    <div class="relative z-10 flex flex-col space-y-6 sm:space-y-8 md:w-2/5 md:gap-6 md:space-y-0 lg:w-1/2">
      <div class="text-left sm:w-9/12 sm:pl-7 md:w-auto md:px-0 lg:pl-0">
        <h3 class="mb-2 text-h3 sm:mb-6 sm:text-h2 md:text-h3">{{ blok.title }}</h3>
        <p class="text-body-2 sm:text-body-1">{{ blok.description }}</p>
      </div>

      <div
        v-if="imageProps.src"
        ref="mobileImageContainerRef"
        :class="mobileImageWrapperClass"
      >
        <div
          v-if="hasParallaxEffect"
          :style="{ transform: mobileImageTransform }"
          class="absolute inset-x-0 -top-[20%] h-[140%] transform-gpu sm:-top-[15%] sm:h-[130%]"
        >
          <NuxtImg
            :src="imageProps.src"
            :alt="imageProps.alt"
            provider="storyblok"
            class="h-full w-full rounded-md object-cover object-left sm:mx-auto sm:w-3/4 sm:object-contain sm:object-center"
          />
        </div>
        <NuxtImg
          v-else
          :src="imageProps.src"
          :alt="imageProps.alt"
          provider="storyblok"
          class="h-full w-full rounded-md object-contain sm:mx-auto sm:w-3/4"
        />
      </div>

      <ButtonLink
        v-if="ctaButton"
        :blok="ctaButton"
        full-width-on-tablet
        class="h-12 md:h-auto xl:h-12"
      />
    </div>

    <div
      v-if="imageProps.src"
      ref="desktopImageContainerRef"
      aria-hidden="true"
      class="pointer-events-none absolute inset-y-0 right-0 hidden w-3/5 md:block lg:w-1/2"
    >
      <div
        v-if="hasParallaxEffect"
        :style="{ transform: desktopImageTransform }"
        class="absolute inset-y-0 -top-[15%] left-16 right-0 h-[130%] transform-gpu"
      >
        <NuxtImg
          :src="imageProps.src"
          :alt="imageProps.alt"
          provider="storyblok"
          class="h-full w-full object-cover object-left"
        />
      </div>
      <div
        v-else
        class="h-full pl-16 lg:pl-10"
      >
        <NuxtImg
          :src="imageProps.src"
          :alt="imageProps.alt"
          provider="storyblok"
          class="h-full w-full object-cover object-left"
        />
      </div>
    </div>
  </div>
</template>
